"use client"

import { useMemo, useState } from "react"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

interface PerformanceDataPoint {
  date: string
  impressions: number
  clicks: number
  leads: number
}

type ChartPoint = {
  date: string
  impressions: number
  clicks: number
  [key: string]: string | number | undefined
}

interface PerformanceChartProps {
  title?: string
  seriesLabels?: Partial<Record<"impressions" | "clicks", string>>
  seriesVisibility?: Partial<Record<"impressions" | "clicks", boolean>>
  data?: ChartPoint[]
  loading?: boolean
  yAxisTickFormatter?: (value: number) => string
  tooltipValueFormatter?: (value: number, dataKey?: "impressions" | "clicks" | string) => string
  tooltipExtraRows?: (point: ChartPoint) => Array<{ label: string; value: string }>
}

export function PerformanceChart({
  title = "Performance Overview",
  seriesLabels = {},
  seriesVisibility = {},
  data: dataProp,
  loading = false,
  yAxisTickFormatter,
  tooltipValueFormatter,
  tooltipExtraRows,
}: PerformanceChartProps) {
  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("30d")
  const labels = {
    impressions: seriesLabels.impressions || "Impressions",
    clicks: seriesLabels.clicks || "Clicks",
  }
  const visibility = {
    impressions: seriesVisibility.impressions ?? true,
    clicks: seriesVisibility.clicks ?? true,
  }
  const sourceData = dataProp ?? []
  const filteredData = useMemo(() => {
    const count = period === "7d" ? 7 : period === "30d" ? 30 : 90
    return sourceData.slice(Math.max(sourceData.length - count, 0))
  }, [period, sourceData])
  const maxValue = filteredData.reduce(
    (max, item) =>
      Math.max(
        max,
        visibility.impressions ? item.impressions || 0 : 0,
        visibility.clicks ? item.clicks || 0 : 0
      ),
    0
  )
  const shouldUseCompactTicks = maxValue >= 1000
  const defaultYAxisTickFormatter = (value: number) =>
    shouldUseCompactTicks ? `${Math.round(value / 100) / 10}k` : String(value)
  const valueFormatter =
    tooltipValueFormatter || ((value: number) => value.toLocaleString())
  const tickFormatter = yAxisTickFormatter || defaultYAxisTickFormatter

  return (
    <Card className="bg-card border-border">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-card-foreground">{title}</CardTitle>
        <Select value={period} onValueChange={(value) => setPeriod(value as "7d" | "30d" | "90d")}>
          <SelectTrigger className="w-[140px] bg-secondary border-border">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">Loading...</div>
          ) : filteredData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">No performance data</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={filteredData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="impressionsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="oklch(0.72 0.19 160)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="oklch(0.72 0.19 160)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="clicksGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="oklch(0.65 0.15 220)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="oklch(0.65 0.15 220)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  stroke="oklch(0.65 0 0)"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="oklch(0.65 0 0)"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                  tickFormatter={(value) => tickFormatter(Number(value))}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const point = payload[0]?.payload as ChartPoint | undefined
                      const extraRows = point && tooltipExtraRows ? tooltipExtraRows(point) : []
                      return (
                        <div className="rounded-lg border border-border bg-popover p-3 shadow-lg">
                          <p className="text-sm font-medium text-popover-foreground mb-2">{label}</p>
                          {payload.map((entry, index) => {
                            const dataKey = String(entry.dataKey ?? "")
                            const labelText =
                              dataKey === "impressions"
                                ? labels.impressions
                                : dataKey === "clicks"
                                  ? labels.clicks
                                  : dataKey
                            return (
                              <div key={index} className="flex items-center gap-2 text-sm">
                                <div
                                  className="h-2 w-2 rounded-full"
                                  style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-muted-foreground capitalize">
                                  {labelText}:
                                </span>
                                <span className="font-medium text-popover-foreground">
                                  {typeof entry.value === "number"
                                    ? valueFormatter(entry.value, dataKey)
                                    : String(entry.value ?? "")}
                                </span>
                              </div>
                            )
                          })}
                          {extraRows.map((row) => (
                            <div key={`${row.label}-${row.value}`} className="flex items-center gap-2 text-sm">
                              <span className="text-muted-foreground">{row.label}:</span>
                              <span className="font-medium text-popover-foreground">{row.value}</span>
                            </div>
                          ))}
                        </div>
                      )
                    }
                    return null
                  }}
                />
                {visibility.impressions ? (
                  <Area
                    type="monotone"
                    dataKey="impressions"
                    stroke="oklch(0.72 0.19 160)"
                    strokeWidth={2}
                    fill="url(#impressionsGradient)"
                  />
                ) : null}
                {visibility.clicks ? (
                  <Area
                    type="monotone"
                    dataKey="clicks"
                    stroke="oklch(0.65 0.15 220)"
                    strokeWidth={2}
                    fill="url(#clicksGradient)"
                  />
                ) : null}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-border">
          {visibility.impressions ? (
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-primary" />
              <span className="text-sm text-muted-foreground">{labels.impressions}</span>
            </div>
          ) : null}
          {visibility.clicks ? (
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-chart-2" />
              <span className="text-sm text-muted-foreground">{labels.clicks}</span>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
